import type { LexicalContent, NodeTypes } from '@mv/core/model/heim/richText';
import { Html } from '@mv/ui-kit/primitives';
import type { ReactNode } from 'react';
import type { RichTextContext, RichTextRenderers } from '../RichText';
import { AudioMedia } from './AudioMedia';
import { VideoMedia } from './VideoMedia';

export type MediaBlockFields = {
	blockType: 'mediaBlock';
	media?: {
		url?: string | null;
		mimeType?: string | null;
		alt?: string | null;
		width?: number | null;
		height?: number | null;
		caption?: LexicalContent | null;
	};
};

export type RenderNodes = (
	nodes: NodeTypes[] | undefined,
	context: RichTextContext,
	renderers?: RichTextRenderers,
) => ReactNode;

export function MediaBlock({
	fields,
	context,
	renderers,
	renderNodes,
}: {
	fields: MediaBlockFields;
	context: RichTextContext;
	renderers: RichTextRenderers | undefined;
	renderNodes: RenderNodes;
}) {
	const media = fields.media;
	if (!media?.url) return null;

	const caption = media.caption?.root?.children;
	const isImage = media.mimeType?.startsWith('image/') ?? true;
	const isVideo = media.mimeType?.startsWith('video/');
	const isAudio = media.mimeType?.startsWith('audio/');

	return (
		<Html.Div className="flex max-w-[600px] flex-col gap-2">
			{isImage ? (
				<Html.Image
					alt={media.alt ?? ''}
					accessible={Boolean(media.alt)}
					accessibilityRole="image"
					accessibilityLabel={media.alt ?? undefined}
					className="h-auto w-full max-w-full"
					resizeMode="contain"
					source={{ uri: media.url }}
					style={{ aspectRatio: media.width && media.height ? media.width / media.height : undefined }}
				/>
			) : isVideo ? (
				<VideoMedia alt={media.alt} uri={media.url} />
			) : isAudio ? (
				<AudioMedia alt={media.alt} uri={media.url} />
			) : (
				<Html.A href={media.url} className="text-link underline">
					{media.alt || media.url}
				</Html.A>
			)}
			{caption && <Html.Div>{renderNodes(caption, context, renderers)}</Html.Div>}
		</Html.Div>
	);
}
